import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

export default function Homepage({ children = "Explore Now ↗" }) {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [activeIcon, setActiveIcon] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const navigate = useNavigate();
  const heroRef = useRef(null);

  // Desktop Shortcuts (Left Side) - Full Menu
  const desktopShortcuts = [
    { id: 'about-us', label: 'About Us', color: '#10B981', type: 'info' },
    { id: 'services', label: 'Services', color: '#3B82F6', type: 'services' },
    { id: 'why-choose-us', label: 'Why Choose Us', color: '#F59E0B', type: 'star' },
    { id: 'products', label: 'Products', color: '#8B5CF6', type: 'box' },
    { id: 'blogs', label: 'Blogs', color: '#EC4899', type: 'book' },
    { id: 'careers', label: 'Careers', color: '#06b6d4', type: 'briefcase' },
    { id: 'contact', label: 'Contact Us', color: '#6366F1', type: 'mail' },
  ];

  // Mac OS Dock Icons (Simplified)
  const dockIcons = [
    { id: 'home', label: 'Finder', color: '#3b82f6', type: 'smile' },
    { id: 'launchpad', label: 'Launchpad', color: '#a855f7', type: 'grid' },
    { id: 'browser', label: 'Safari', color: '#0ea5e9', type: 'compass' },
    { id: 'settings', label: 'Settings', color: '#94a3b8', type: 'settings' },
  ];

  // Words for rotation (Lighter colors for dark mode)
  const rotatingWords = [
    { text: "Productivity", color: "#a78bfa", bg: "rgba(139, 92, 246, 0.2)" },
    { text: "Reliability", color: "#60a5fa", bg: "rgba(59, 130, 246, 0.2)" },
    { text: "Creativity", color: "#34d399", bg: "rgba(16, 185, 129, 0.2)" },
    { text: "Innovative", color: "#fbbf24", bg: "rgba(245, 158, 11, 0.2)" },
    { text: "Team Work", color: "#f472b6", bg: "rgba(236, 72, 153, 0.2)" }
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePos({
          x: (e.clientX - rect.left) / rect.width - 0.5,
          y: (e.clientY - rect.top) / rect.height - 0.5
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prevIndex) =>
          prevIndex === rotatingWords.length - 1 ? 0 : prevIndex + 1
        );
        setTimeout(() => setIsAnimating(false), 100);
      }, 500);
    }, 3800);

    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(timeInterval);
    };
  }, [rotatingWords.length]);

  const handleShortcutClick = (id) => {
    if (id === 'contact') {
      navigate('/contactus');
      return;
    }
    if (id === 'careers') {
      navigate('/careers');
      return;
    }


    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  const renderIcon = (type) => {
    switch (type) {
      case 'info': return <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="currentColor" />;
      case 'services': return <path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.66 0-3 1.34-3 3 0 .35.07.69.18 1H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM12 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" fill="currentColor" />;
      case 'star': return <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" fill="currentColor" />;
      case 'box': return <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 9h-2V7h-2v5H6v2h2v5h2v-5h2v-2z" fill="currentColor" />;
      case 'book': return <path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z" fill="currentColor" />;
      case 'mail': return <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor" />;
      case 'briefcase': return <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" fill="currentColor" />;
      case 'grid': return <path d="M4 4h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 10h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 16h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4z" fill="currentColor" />;
      case 'user': return <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor" />;
      case 'smile': return <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" fill="currentColor" />;
      case 'compass': return <path d="M12 10.9c-.61 0-1.1.49-1.1 1.1s.49 1.1 1.1 1.1c.61 0 1.1-.49 1.1-1.1s-.49-1.1-1.1-1.1zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm2.19 12.19L6 18l3.81-8.19L18 6l-3.81 8.19z" fill="currentColor" />;
      case 'settings': return <path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.5.45l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.04.27.25.45.5.45h4c.25 0 .46-.18.5-.45l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" fill="currentColor" />;
      default: return null;
    }
  };

  return (
    <div
      ref={heroRef}
      className="homepage-container"
      style={{
        backgroundColor: '#0f172a', // Dark Background
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        fontFamily: "'Inter', 'Plus Jakarta Sans', sans-serif"
      }}
    >
      {/* --- Mac OS Top Bar (Absolute Positioning) --- */}
      <div className="mac-top-bar">
        <div className="top-bar-left">
          <span className="top-menu-item">Author</span>
          <span className="top-menu-item">Showcase</span>
          <span className="top-menu-item">View</span>
          <span className="top-menu-item">Help</span>
        </div>
        <div className="top-bar-right">
          <span className="top-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" /></svg>
          </span>
          <div className="top-clock">
            <span className="top-date">{currentTime.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</span>
            <span className="top-time">{currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
          </div>
        </div>
      </div>

      {/* --- Dynamic Wallpaper Visuals --- */}
      <div className="desktop-wallpaper">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="bg-video"
          src="/images/Network_Solutions_Page_Banner_Background_Video_5_720P.mp4"
        />

        <div className="grid-overlay"></div>
        <div className="noise-overlay"></div>
        <div className="glass-layer"></div>

        {/* Enriched Abstract Shapes (Removed for Video clarity or reduced opacity) */}
        {/* <div className="abstract-shape shape-1"></div> */}

        {/* Right Side Tech Grid Visuals */}
        <div className="right-side-visuals">
          <div className="tech-ring ring-1"></div>
          <div className="tech-ring ring-2"></div>
          <div className="tech-grid-dots"></div>
        </div>

      </div>

      {/* --- Background Text --- */}
      <div className="background-text-container">
        <div className="text-wrapper">
          <h2 className="bg-text">
            We craft end-to-end solutions from concept
          </h2>
          <h2 className="bg-text">
            to final product with
            <span
              className={`rotating-text-box ${isAnimating ? 'animating' : ''}`}
              style={{
                color: rotatingWords[currentWordIndex].color,
                backgroundColor: rotatingWords[currentWordIndex].bg,
                boxShadow: `0 0 30px ${rotatingWords[currentWordIndex].bg}`,
                borderRadius: '12px',
                width: '280px'
              }}
            >
              {rotatingWords[currentWordIndex].text}
            </span>
          </h2>
        </div>
        <p className="bg-subtitle">We excel in avant-garde technologies</p>
      </div>

      {/* --- Left Side Desktop Shortcuts (Icon Only) --- */}
      <div className="desktop-shortcuts-container">
        {desktopShortcuts.map(shortcut => (
          <div
            key={shortcut.id}
            className="desktop-shortcut"
            onClick={() => handleShortcutClick(shortcut.id)}
            onMouseEnter={() => setActiveIcon(shortcut.id)}
            onMouseLeave={() => setActiveIcon(null)}
          >
            <div className="shortcut-icon glass-effect" style={{ '--accent-color': shortcut.color }}>
              <div className="icon-inner" style={{ backgroundColor: shortcut.color }}>
                <svg viewBox="0 0 24 24" className="shortcut-svg">{renderIcon(shortcut.type)}</svg>
              </div>
            </div>
            <span className="shortcut-tooltip">{shortcut.label}</span>
          </div>
        ))}
      </div>

      {/* --- Right Side Widgets (Expanded) --- */}
      <div className="desktop-widgets-container">
        {/* Calendar Widget */}
        <div className="desktop-widget calendar-widget glass-card">
          <div className="widget-header">
            <span className="widget-month">{currentTime.toLocaleString('default', { month: 'long' })}</span>
            <span className="widget-year">{currentTime.getFullYear()}</span>
          </div>
          <div className="widget-day-big">{currentTime.getDate()}</div>
          <div className="widget-weekday">{currentTime.toLocaleString('default', { weekday: 'long' })}</div>
        </div>

        {/* Projects Widget (System Status Removed) */}
        <div className="desktop-widget status-widget glass-card">
          <div className="status-item" style={{ justifyContent: 'center', width: '100%' }}>
            <div className="status-info" style={{ alignItems: 'center', textAlign: 'center' }}>
              <span className="status-label" style={{ fontSize: '11px', letterSpacing: '1px' }}>Total Projects</span>
              <span className="status-value" style={{ fontSize: '20px', marginTop: '4px' }}>80+ Delivered</span>
            </div>
          </div>
        </div>

        {/* Client Retention Widget */}
        <div className="desktop-widget storage-widget glass-card">
          <div className="widget-title">Client Retention</div>
          <div className="storage-chart">
            <svg viewBox="0 0 36 36" className="circular-chart">
              <path className="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              <path className="circle" strokeDasharray="93, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            </svg>
            <div className="storage-text">
              <span className="storage-percent">93%</span>
              <span className="storage-label">Retained</span>
            </div>
          </div>
        </div>

        {/* Support Widget */}
        <div className="desktop-widget glass-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <div className="widget-title" style={{ marginBottom: '5px' }}>Support Available</div>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '5px' }}>
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
          <span style={{ fontSize: '24px', fontWeight: '800', color: '#e2e8f0' }}>24/7</span>
        </div>

      </div>

      {/* --- Simplified Mac OS Dock (Footer) --- */}
      <div className="dock-container">
        <div className="premium-dock glass-panel">
          {dockIcons.map(icon => (
            <div
              key={icon.id}
              className="dock-item"
              onClick={() => handleShortcutClick(icon.id)}
            >
              <div className="dock-icon-box" style={{ backgroundColor: icon.color }}>
                <svg viewBox="0 0 24 24" className="dock-svg">{renderIcon(icon.type)}</svg>
              </div>
              <div className="dock-tooltip">{icon.label}</div>
              <div className="dock-reflection"></div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&family=Inter:wght@400;500;600&display=swap');
        
        /* Mac OS Top Bar (Absolute - Not Fixed) */
        .homepage-container .mac-top-bar {
            position: absolute; /* Changed from fixed */
            top: 0; left: 0; right: 0;
            height: 32px;
            background: rgba(15, 23, 42, 0.4); 
            backdrop-filter: blur(10px);
            z-index: 10;
            display: flex; justify-content: space-between; align-items: center;
            padding: 0 20px;
            border-bottom: 1px solid rgba(255,255,255,0.05);
            font-family: 'Inter', sans-serif;
            font-size: 13px; font-weight: 500; color: #cbd5e1;
        }
        .top-bar-left, .top-bar-right { display: flex; align-items: center; gap: 16px; }
        .apple-logo { font-size: 16px; margin-right: 4px; color: #fff; }
        .top-menu-item { cursor: pointer; opacity: 0.9; hover: { opacity: 1; color: #fff; } }
        .top-menu-item:first-of-type { font-weight: 700; color: #fff; }
        .top-clock { display: flex; gap: 8px; font-weight: 600; color: #e2e8f0; }
        .top-icon { opacity: 0.8; }

        .desktop-wallpaper {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background: #000; /* Fallback Black */
          overflow: hidden;
        }

        .bg-video {
            position: absolute; inset: 0; width: 100%; height: 100%;
            object-fit: cover; opacity: 0.6; /* Adjust visibility */
        }

        /* Noise Texture */
        .noise-overlay {
            position: absolute; inset: 0; opacity: 0.04;
            background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opactiy='1'/%3E%3C/svg%3E");
            pointer-events: none;
        }

        /* Glass Layer */
        .glass-layer {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 80%; height: 60%;
          background: rgba(255, 255, 255, 0.03); 
          backdrop-filter: blur(80px);
          border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
          filter: blur(60px); opacity: 0.5;
          animation: fluidMorph 20s infinite alternate ease-in-out;
          z-index: 0;
        }

        /* Animated Grid Overlay */
        .grid-overlay {
          position: absolute; inset: -50%; width: 200%; height: 200%;
          background-image: 
            linear-gradient(rgba(59, 130, 246, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
          transform: perspective(1000px) rotateX(60deg);
          animation: gridPan 40s linear infinite;
        }
        @keyframes gridPan {
          0% { transform: perspective(1000px) rotateX(60deg) translateY(0); }
          100% { transform: perspective(1000px) rotateX(60deg) translateY(50px); }
        }
        
        /* Right Side Visuals */
        .right-side-visuals {
            position: absolute; top: 100px; right: 50px; width: 300px; height: 600px; pointer-events: none; z-index: 1;
        }
        .tech-ring {
            position: absolute; border-radius: 50%; border: 1px solid rgba(59, 130, 246, 0.1);
            top: 50%; left: 50%; transform: translate(-50%, -50%);
        }
        .ring-1 { width: 400px; height: 400px; border-style: dashed; animation: spin 40s linear infinite; }
        .ring-2 { width: 500px; height: 500px; border: 1px solid rgba(139, 92, 246, 0.05); animation: spin 60s reverse linear infinite; }
        .tech-grid-dots {
            position: absolute; top: 0; right: 0; width: 200px; height: 400px;
            background-image: radial-gradient(#3b82f6 1px, transparent 1px);
            background-size: 20px 20px; opacity: 0.1;
        }
        
        @keyframes spin { 100% { transform: translate(-50%, -50%) rotate(360deg); } }

        /* Abstract Shapes */
        .abstract-shape {
          position: absolute; border-radius: 50%; filter: blur(100px); opacity: 0.2;
          animation: fluidMorph 15s infinite alternate ease-in-out;
        }
        .shape-1 { width: 600px; height: 600px; background: rgba(59, 130, 246, 0.1); top: -200px; right: -200px; }
        .shape-2 { width: 500px; height: 500px; background: rgba(16, 185, 129, 0.1); bottom: -100px; left: -100px; animation-delay: -4s; }
        .shape-3 { width: 300px; height: 300px; background: rgba(236, 72, 153, 0.1); top: 15%; left: 15%; animation-delay: -2s; }
        
        /* Premium Text Layout */
        .background-text-container {
          position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
          z-index: 2; pointer-events: none; padding: 20px; text-align: center;
          max-width: 60%; margin: 0 auto; /* Prevent overlap */
        }
        .text-wrapper { margin-bottom: 2.5rem; }
        
        .bg-text {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 40px; font-weight: 800; color: #f8fafc;
          line-height: 1.25; margin: 0; letter-spacing: -0.03em;
          text-shadow: 0 4px 50px rgba(0,0,0,0.5);
        }

        .rotating-text-box {
            display: inline-block; margin-left: 14px; padding: 2px 0; /* Removed horizontal padding */
            transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
            vertical-align: middle; line-height: 1.4;
            /* backdrop-filter: blur(8px); Removed */
            /* color will be applied inline */
            transform: translateY(0) scale(1);
            opacity: 1;
        }

        .rotating-text-box.animating {
            transform: translateY(20px) scale(0.9);
            opacity: 0;
            filter: blur(4px);
        }
        
        .bg-subtitle {
          font-family: 'Inter', sans-serif; font-size: 1.15rem; color: #cbd5e1; font-weight: 500;
          letter-spacing: 0.01em; background: rgba(255,255,255,0.05); padding: 8px 16px;
          border-radius: 50px; backdrop-filter: blur(5px); border: 1px solid rgba(255,255,255,0.1);
        }

        /* Glassmorphism Utilities */
        .glass-card {
            background: rgba(30, 41, 59, 0.4); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.08);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 10px 15px -3px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.05);
        }

        /* Desktop Shortcuts (Left Side) */
        .desktop-shortcuts-container {
          position: absolute; top: 50%; left: 30px; transform: translateY(-50%); /* Center Vertically */
          z-index: 100;
          display: flex; flex-direction: column; gap: 20px;
        }
        
        .desktop-shortcut {
          position: relative; display: flex; flex-direction: column; align-items: center;
          cursor: pointer; width: 60px; transition: all 0.2s ease;
        }
        .desktop-shortcut:hover { transform: scale(1.1); }
        .desktop-shortcut:active { transform: scale(0.95); }
        
        .shortcut-icon {
          width: 50px; height: 50px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.08); backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.1); box-shadow: 0 4px 15px rgba(0,0,0,0.2);
          transition: all 0.3s ease;
        }
        .desktop-shortcut:hover .shortcut-icon {
           background: rgba(255,255,255,0.15);
           box-shadow: 0 8px 25px rgba(var(--accent-color), 0.3);
           border-color: rgba(var(--accent-color), 0.5);
        }
        
        .icon-inner {
            width: 36px; height: 36px; border-radius: 10px;
            display: flex; align-items: center; justify-content: center; color: white;
            box-shadow: inset 0 2px 4px rgba(255,255,255,0.2), 0 4px 8px rgba(0,0,0,0.3);
        }
        
        .shortcut-svg { width: 20px; height: 20px; filter: drop-shadow(0 2px 2px rgba(0,0,0,0.5)); }
        
        .shortcut-tooltip {
          position: absolute; left: 70px; top: 50%; transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.8); color: white; padding: 6px 12px; border-radius: 6px;
          font-size: 12px; font-weight: 600; opacity: 0; visibility: hidden; transition: all 0.2s;
          pointer-events: none; white-space: nowrap; backdrop-filter: blur(4px); z-index: 101;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .desktop-shortcut:hover .shortcut-tooltip { opacity: 1; visibility: visible; left: 65px; }

        /* Widgets Container */
        .desktop-widgets-container {
            position: absolute; top: 80px; right: 50px; z-index: 10;
            display: flex; flex-direction: column; gap: 20px; width: 280px;
        }
        
        .desktop-widget {
            border-radius: 24px; padding: 20px;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .desktop-widget:hover { transform: translateY(-5px); box-shadow: 0 20px 40px rgba(0,0,0,0.3); }

        .calendar-widget { text-align: center; }
        .widget-header { display: flex; justify-content: space-between; font-size: 14px; font-weight: 700; color: #94a3b8; margin-bottom: 12px; }
        .widget-day-big { font-size: 3.5rem; font-weight: 800; color: #e2e8f0; line-height: 1; }
        .widget-weekday { font-size: 1.1rem; font-weight: 600; color: #60a5fa; margin-top: 4px; }

        .status-widget { padding: 16px 20px; }
        .status-item { display: flex; align-items: center; gap: 14px; }
        .status-dot { width: 10px; height: 10px; border-radius: 50%; box-shadow: 0 0 10px rgba(16, 185, 129, 0.6); position: relative; }
        .status-dot.online { background: #10B981; }
        .status-info { display: flex; flex-direction: column; }
        .status-label { font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 700; margin-bottom: 2px; }
        .status-value { font-size: 14px; font-weight: 700; color: #cbd5e1; }
        .status-divider { height: 1px; background: rgba(255,255,255,0.08); margin: 12px 0; }
        
        .widget-title { font-size: 13px; font-weight: 700; color: #94a3b8; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.05em; }

        /* Storage Widget */
        .storage-widget { display: flex; flex-direction: column; align-items: center; padding: 15px; }
        .storage-chart { position: relative; width: 100px; height: 100px; }
        .circular-chart { display: block; margin: 0 auto; max-width: 100%; max-height: 100%; }
        .circle-bg { fill: none; stroke: rgba(255,255,255,0.1); stroke-width: 3.8; }
        .circle { fill: none; stroke-width: 2.8; stroke-linecap: round; stroke: #8b5cf6; animation: progress 1s ease-out forwards; }
        .storage-text { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; }
        .storage-percent { display: block; font-size: 18px; font-weight: 700; color: #e2e8f0; }
        .storage-label { font-size: 10px; color: #94a3b8; }
        
        /* Network Widget */
        .network-widget { padding: 15px; }
        .network-graph { display: flex; align-items: flex-end; justify-content: space-between; height: 40px; margin-bottom: 5px; gap: 3px; }
        .bar { width: 100%; background: #3b82f6; border-radius: 2px; opacity: 0.8; animation: bounce 1.2s infinite ease-in-out; }
        .network-speed { font-size: 16px; font-weight: 700; color: #e2e8f0; float: right; }
        
        @keyframes bounce { 0%, 100% { transform: scaleY(0.7); opacity: 0.6; } 50% { transform: scaleY(1); opacity: 1; } }

        /* Premium Floating Dock */
        .dock-container {
            position: absolute; bottom: 30px; left: 0; right: 0;
            display: flex; justify-content: center; pointer-events: none; z-index: 100;
        }
        
        .premium-dock {
            pointer-events: auto; display: flex; align-items: center; gap: 16px; padding: 12px 24px;
            background: rgba(15, 23, 42, 0.6); backdrop-filter: blur(30px);
            border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 28px;
            box-shadow: 0 20px 50px -10px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
            transition: all 0.3s ease;
        }
        
        .dock-item { position: relative; cursor: pointer; transition: transform 0.2s cubic-bezier(0.25, 1.5, 0.5, 1); }
        .dock-item:hover { transform: translateY(-10px) scale(1.1); margin: 0 6px; }
        
        .dock-icon-box {
            width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: white;
            box-shadow: 0 4px 10px rgba(0,0,0,0.3), inset 0 2px 4px rgba(255,255,255,0.2);
        }
        .dock-svg { width: 24px; height: 24px; filter: drop-shadow(0 2px 2px rgba(0,0,0,0.4)); }
        
        .dock-tooltip {
            position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%) translateY(10px);
            background: rgba(0, 0, 0, 0.9); color: white; padding: 5px 12px; border-radius: 8px;
            font-size: 12px; font-weight: 500; opacity: 0; pointer-events: none; transition: 0.2s; visibility: hidden; margin-bottom: 12px;
            white-space: nowrap; backdrop-filter: blur(4px);
        }
        .dock-item:hover .dock-tooltip { opacity: 1; transform: translateX(-50%) translateY(0); visibility: visible; }
        
        /* Mobile Adjustments */
        /* Homepage Container - Desktop Defaults */
        .homepage-container {
            overflow: hidden;
            height: 100vh;
        }

        /* Responsive Styles */
        @media (max-width: 991px) {
           .homepage-container {
               overflow-y: auto !important;
               overflow-x: hidden !important;
               height: auto;
               /* Allow scrolling on mobile */
           }

           /* Background Absolute, Not Fixed to avoid bleeding to other sections */
           .desktop-wallpaper {
               position: absolute;
               height: 100%;
               width: 100%;
           }

           /* Text Section - Stacked */
           .background-text-container {
               position: relative;
               inset: auto;
               padding: 100px 20px 30px; /* Below Top Bar */
               max-width: 100%;
               z-index: 1;
           }
           .bg-text { font-size: 2rem; text-shadow: 0 2px 20px rgba(0,0,0,0.5); }
           .rotating-text-box { margin-left: 8px; }

           /* Shortcuts - Hidden on Mobile */
           .desktop-shortcuts-container { 
             display: none;
           }
           
           /* Widgets - Below Text */
           .desktop-widgets-container {
             position: relative; top: auto; right: auto; transform: none;
             width: 100%; flex-direction: row; flex-wrap: wrap; justify-content: center;
             padding: 0 20px 140px; /* Space for Dock */
             gap: 20px;
             z-index: 2;
           }
           
           /* Widgets Size */
           .desktop-widget { width: 45%; min-width: 280px; }
           
           /* Dock - Fixed Bottom */
           .dock-container { bottom: 20px; width: 95%; left: 50%; transform: translateX(-50%); z-index: 1000; }
           .premium-dock { overflow-x: auto; max-width: 100%; padding: 10px 15px; justify-content: center; }
           .premium-dock::-webkit-scrollbar { display: none; }
           .dock-tooltip { display: none; }

           /* Top Bar Fixed */
           .mac-top-bar { padding: 0 16px; position: fixed; background: rgba(15, 23, 42, 0.9); z-index: 1100; }
           .top-menu-item { display: none; } 
           .top-menu-item:first-of-type { display: block; } 
           .right-side-visuals { display: none; }
        }
        
        @media (max-width: 600px) {
            .bg-text { font-size: 1.75rem; }
            .desktop-widget { width: 100%; }
            .premium-dock { gap: 12px; justify-content: flex-start; }
            .dock-icon-box { width: 44px; height: 44px; }
            .dock-svg { width: 22px; height: 22px; }
        }
      `}</style>
    </div>
  );
}